import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, CheckCircle, XCircle, Settings, Download, X } from 'lucide-react';
import './AdminVendors.css';
import { API_BASE_URL } from '../../api';

const AdminProducts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', product_images: [], unit: 'piece', weight: '', hsn_code: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: ''
  });
  const [uploadingImages, setUploadingImages] = useState(false);

  const vendorIdLookup = useMemo(() => {
    const lookup = {};
    vendors.forEach(vendor => {
      if (!vendor || vendor.id == null) return;
      lookup[vendor.id] = vendor.vendor_id || vendor.vendor_code || (`#${vendor.id}`);
    });
    return lookup;
  }, [vendors]);

  // helpers for currency + discount display
  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toFixed(2);
  };

  const getDiscountDetails = (price, mrp) => {
    const priceNum = Number(price);
    const mrpNum = Number(mrp);
    const valid = Number.isFinite(priceNum) && priceNum > 0 && Number.isFinite(mrpNum) && mrpNum > 0;
    if (!valid || mrpNum <= priceNum) return null;
    const amount = mrpNum - priceNum;
    const percent = (amount / mrpNum) * 100;
    return {
      amount,
      percent: percent.toFixed(percent >= 10 ? 0 : 1)
    };
  };

  const discountPreview = useMemo(
    () => getDiscountDetails(formData.price, formData.mrp),
    [formData.price, formData.mrp]
  );

  // simple toast notifications
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const adminHeaders = () => {
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') ||
      window.localStorage.getItem('adminApiKey') ||
      process.env.REACT_APP_ADMIN_API_KEY ||
      'dev-admin-key-sonic-cart-2024-secure';
    const headers = {};
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    return headers;
  };

  const getCategoryName = (category) => {
    if (!category) return '';
    return category.name || category.category_name || category.title || '';
  };

  const getCategoryId = (category) => {
    if (!category) return '';
    return category.id ?? category.category_id ?? category._id ?? getCategoryName(category);
  };

  const normalizeCategories = (payload) => {
    const rawCategories = Array.isArray(payload)
      ? payload
      : (payload?.categories || payload?.items || payload?.data?.categories || payload?.data || payload?.result || []);

    if (!Array.isArray(rawCategories)) return [];

    return rawCategories
      .map((category, index) => {
        if (!category || typeof category !== 'object') return null;

        const categoryName = getCategoryName(category).toString().trim();
        if (!categoryName) return null;
        const resolvedId = getCategoryId(category);

        return {
          ...category,
          id: resolvedId || `${categoryName}-${index}`,
          name: categoryName,
          hsn_code: category?.hsn_code ?? category?.hsn ?? category?.hsnCode ?? ''
        };
      })
      .filter(Boolean);
  };

  // Download image function
  const downloadImage = async (imageUrl, productName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from URL or use product name
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || `${productName.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Image downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading image:', error);
      showToast('Failed to download image', 'error');
    }
  };

  const fetchVendors = async () => {
    try {
      // pull a large batch so we can map vendor IDs for the product table lookup
      const res = await fetch(`${API_BASE_URL}/api/admin/vendors?limit=5000`, { headers: adminHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVendors(data.vendors || data.items || []);
      }
    } catch (_) {}
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`, { headers: adminHeaders() });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data.error || data.message || `Failed to fetch categories (${response.status})`;
        throw new Error(message);
      }

      const data = await response.json();
      const normalized = normalizeCategories(data);
      setCategories(normalized);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      showToast(error.message || 'Failed to fetch categories', 'error');
    }
  };

  // Handle category change with automatic HSN and SKU population
  const handleCategoryChange = async (e) => {
    const { value } = e.target;
    
    if (value) {
      // Find the selected category to get HSN data
      const selectedCategory = categories.find(cat => getCategoryName(cat) === value);
      
      if (selectedCategory) {
        // Get vendor ID from form data
        const vendorId = formData.vendor_id;

        // Generate category code (first 3 letters of category name, uppercase)
        const categoryCode = value.substring(0, 3).toUpperCase();
        
        // In edit mode, only generate new SKU if the category actually changed
        let sku = formData.sku; // Keep existing SKU by default
        
        // Only generate new SKU if:
        // 1. We're not in edit mode (editingProduct is null), OR
        // 2. The category actually changed from the original
        const originalCategoryName = editingProduct ? (editingProduct.category || editingProduct.category_name || '') : '';
        const shouldGenerateNewSku = !editingProduct || 
          (editingProduct && originalCategoryName !== value);
        
        if (shouldGenerateNewSku) {
          // Generate SKU using the backend endpoint for proper sequencing
          if (vendorId) {
            try {
              const response = await fetch(`${API_BASE_URL}/api/products/next-sku/${vendorId}/${categoryCode}`);
              if (response.ok) {
                const data = await response.json();
                sku = data.nextSku;
              } else {
                // Fallback to basic SKU if API fails
                sku = `SONI-${vendorId}-${categoryCode}-0001`;
              }
            } catch (error) {
              console.error('Error fetching next SKU:', error);
              // Fallback to basic SKU if API fails
              sku = `SONI-${vendorId}-${categoryCode}-0001`;
            }
          } else {
            // Generate a placeholder SKU that will be updated when vendor is selected
            sku = `SONI-XXX-${categoryCode}-0001`;
          }
        }

        // Auto-populate HSN and SKU (if new SKU was generated)
        setFormData(prev => ({
          ...prev,
          category: value,
          hsn_code: selectedCategory.hsn_code || '',
          sku: sku
        }));
      }
    } else {
      // Clear related fields when category is deselected
      setFormData(prev => ({
        ...prev,
        category: value,
        hsn_code: '',
        sku: ''
      }));
    }
  };

  // Handle vendor change - regenerate SKU if category is already selected
  const handleVendorChange = async (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, vendor_id: value }));
    
    // If category is already selected, regenerate SKU
    if (formData.category && value) {
      const selectedCategory = categories.find(cat => getCategoryName(cat) === formData.category);
      if (selectedCategory) {
        const categoryCode = formData.category.substring(0, 3).toUpperCase();
        let sku = '';
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/products/next-sku/${value}/${categoryCode}`);
          if (response.ok) {
            const data = await response.json();
            sku = data.nextSku;
          } else {
            sku = `SONI-${value}-${categoryCode}-0001`;
          }
        } catch (error) {
          console.error('Error fetching next SKU on vendor change:', error);
          sku = `SONI-${value}-${categoryCode}-0001`;
        }
        
        setFormData(prev => ({ ...prev, sku }));
      }
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams();
      const trimmedSearch = (searchQuery || '').trim();
      if (/^PRDT\d{3,}$/i.test(trimmedSearch)) {
        qs.set('product_id', trimmedSearch.toUpperCase());
      } else if (searchQuery) {
        qs.set('search', searchQuery);
      }
      if (categoryFilter && categoryFilter !== 'all') qs.set('category', categoryFilter);
      if (statusFilter && statusFilter !== 'all') qs.set('status', statusFilter);
      // If the search text looks like a vendor code VDR###, filter by vendor_code
      if (/^VDR\d{3,}$/i.test(trimmedSearch)) {
        qs.set('vendor_code', trimmedSearch.toUpperCase());
      } else if (/^\d+$/.test(trimmedSearch)) {
        // numeric vendor internal id
        qs.set('vendor_id', trimmedSearch);
      } else if (vendorFilter && vendorFilter !== 'all') {
        qs.set('vendor_id', vendorFilter);
      }
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      qs.set('page', String(page));
      qs.set('limit', String(limit));
      const res = await fetch(`${API_BASE_URL}/api/admin/products?${qs.toString()}`, { headers: adminHeaders() });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load products');
      }
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [searchQuery, categoryFilter, statusFilter, vendorFilter, dateFrom, dateTo, page, limit]);

  // Scroll to edit form when editing starts
  useEffect(() => {
    if (editingProduct) {
      setTimeout(() => {
        const editRow = document.querySelector('.inline-edit-form-row');
        if (editRow) {
          editRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  }, [editingProduct]);

  const openAdd = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', product_images: [], unit: 'piece', weight: '', hsn_code: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', product_images: [], unit: 'piece', weight: '', hsn_code: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: '' });
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      // Close modal on Escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeModal();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showModal]);

  const openEdit = async (p) => {
    console.log('Opening edit for product:', p);
    setEditingProduct(p);
    const productCategoryName = p.category || p.category_name || '';
    const productCategoryId = p.category_id ?? p.categoryId;
    const matchedCategory = categories.find((cat) => {
      if (productCategoryName && getCategoryName(cat) === productCategoryName) return true;
      if (productCategoryId !== undefined && productCategoryId !== null && String(getCategoryId(cat)) === String(productCategoryId)) return true;
      return false;
    });
    const resolvedCategory = matchedCategory ? getCategoryName(matchedCategory) : productCategoryName;
    const parsedImages = (() => {
      if (Array.isArray(p.product_images) && p.product_images.length) return p.product_images;
      if (typeof p.product_images === 'string' && p.product_images.trim()) {
        try {
          const parsed = JSON.parse(p.product_images);
          if (Array.isArray(parsed)) return parsed;
        } catch (_) {}
      }
      if (p.image_url) return [p.image_url];
      return [];
    })();
    const nextFormData = {
      name: p.name || '',
      description: p.description || '',
      sku: p.sku || '',
      category: resolvedCategory || '',
      price: p.price || '',
      mrp: p.mrp || '',
      cost_price: p.cost_price || '',
      image_url: p.image_url || (parsedImages[0] || ''),
      product_images: parsedImages,
      unit: p.unit || 'piece',
      weight: p.weight || '',
      hsn_code: p.hsn_code || '',
      stock_on_hand: p.stock_on_hand || 0,
      min_stock_level: p.min_stock_level || 0,
      reorder_point: p.reorder_point || 0,
      reorder_quantity: p.reorder_quantity || 0,
      vendor_id: p.vendor_id || ''
    };
    console.log('Setting form data:', nextFormData);
    setFormData(nextFormData);
    // Don't show modal for editing - form will appear inline
    
    // Trigger auto-population if category is selected and we have category data
    if (nextFormData.category && categories.length > 0) {
      const selectedCategory = categories.find(cat => getCategoryName(cat) === nextFormData.category);
      console.log('Found category for auto-population:', selectedCategory);
      if (selectedCategory) {
        // Update HSN from category data (but keep existing SKU)
        setFormData(prev => ({
          ...prev,
          hsn_code: selectedCategory.hsn_code || prev.hsn_code
        }));
      }
    }
  };

  const handleImageUpload = async (event) => {
    const inputEl = event.target;
    const files = Array.from(inputEl.files || []);
    if (!files.length) return;

    setUploadingImages(true);
    try {
      const headers = adminHeaders();
      const uploads = await Promise.all(files.map(async (file) => {
        const form = new FormData();
        form.append('image', file);
        const res = await fetch(`${API_BASE_URL}/api/admin/upload/product-image`, {
          method: 'POST',
          headers,
          body: form
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to upload image');
        }
        const data = await res.json();
        return data.imageUrl;
      }));

      setFormData(prev => {
        const nextImages = [...(prev.product_images || []), ...uploads];
        return {
          ...prev,
          product_images: nextImages,
          image_url: nextImages[0] || prev.image_url
        };
      });
      showToast('Images uploaded successfully', 'success');
    } catch (error) {
      console.error('Image upload error:', error);
      showToast(error.message || 'Failed to upload images', 'error');
    } finally {
      setUploadingImages(false);
      if (inputEl) inputEl.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const nextImages = (prev.product_images || []).filter((_, i) => i !== index);
      return {
        ...prev,
        product_images: nextImages,
        image_url: nextImages[0] || ''
      };
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      console.log('Saving product:', { editingProduct, formData });
      
      const isAdmin = true;
      if (isAdmin && !formData.vendor_id) {
        showToast('Please select a vendor', 'error');
        return;
      }
      
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct ? `${API_BASE_URL}/api/admin/products/${editingProduct.id}` : `${API_BASE_URL}/api/admin/products`;
      
      console.log('API Request:', { method, url, formData });
      
      const payload = {
        ...formData,
        product_images: Array.isArray(formData.product_images) ? formData.product_images : [],
        image_url: formData.image_url || (Array.isArray(formData.product_images) && formData.product_images.length ? formData.product_images[0] : '')
      };

      const res = await fetch(url, { 
        method, 
        headers: { 'Content-Type': 'application/json', ...adminHeaders() }, 
        body: JSON.stringify(payload) 
      });
      
      console.log('API Response:', { status: res.status, ok: res.ok });
      
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        console.error('API Error:', j);
        throw new Error(j.error || 'Failed to save product');
      }
      
      const result = await res.json();
      console.log('API Success:', result);
      
      if (editingProduct) {
        setEditingProduct(null);
        setFormData({ name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', product_images: [], unit: 'piece', weight: '', hsn_code: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: '' });
      } else {
        closeModal();
      }
      fetchProducts();
      showToast(editingProduct ? 'Product updated' : 'Product created', 'success');
    } catch (e) {
      console.error('Save product error:', e);
      showToast(e.message, 'error');
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/products/${p.id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ status: next }) });
      if (!res.ok) throw new Error('Failed to update status');
      fetchProducts();
      showToast(`Product ${next === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this product permanently?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/products/${id}`, { method: 'DELETE', headers: adminHeaders() });
      if (!res.ok) throw new Error('Failed to delete product');
      fetchProducts();
      showToast('Product deleted', 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const runBulk = async () => {
    if (selectedProducts.length === 0 || !bulkAction) return;
    try {
      if (bulkAction === 'delete') {
        if (!window.confirm(`Permanently delete ${selectedProducts.length} products?`)) return;
        const res = await fetch(`${API_BASE_URL}/api/admin/products/bulk-delete`, { method: 'DELETE', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ productIds: selectedProducts }) });
        if (!res.ok) throw new Error('Bulk delete failed');
      } else {
        const res = await fetch(`${API_BASE_URL}/api/admin/products/bulk-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ productIds: selectedProducts, status: bulkAction }) });
        if (!res.ok) throw new Error('Bulk status failed');
      }
      setSelectedProducts([]);
      setBulkAction('');
      setShowBulkActions(false);
      fetchProducts();
      showToast('Bulk action completed', 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleSelection = (id) => {
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedProducts(products.map(p => p.id));
  const clearSel = () => setSelectedProducts([]);

  return (
    <div className="admin-vendors">
      <div className="admin-vendors-header">
        <div className="admin-vendors-header-content">
          <h1>Product Management</h1>
          <p>Manage all products across vendors</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={20} />
          Add Product
        </button>
      </div>

      <div className="admin-vendors-filters">
        <div className="admin-filters-left" style={{ flexWrap: 'wrap' }}>
          <div className="admin-search-container">
            <Search size={20} className="admin-search-icon" />
            <input type="text" placeholder="Search by name, SKU, Product ID (e.g. PRDT025), vendor or vendor ID" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }} className="admin-search-input" />
          </div>
          <div className="admin-filter-group">
            <Filter size={20} />
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} className="admin-filter-select">
              <option value="all">All Categories</option>
              <option value="Food & Beverages">FAB001 - Food & Beverages</option>
              <option value="Fresh Produce">FPD001 - Fresh Produce</option>
              <option value="Dairy">DARY001 - Dairy</option>
              <option value="Bakery">BKRY001 - Bakery</option>
              <option value="Pantry">PTRY001 - Pantry</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="admin-filter-input" />
            <span style={{ margin: '0 6px' }}>to</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="admin-filter-input" />
          </div>
          <div className="admin-filter-group">
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="admin-filter-select" style={{ minWidth: 140 }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <select 
              value={vendorFilter} 
              onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }} 
              className="admin-filter-select"
              style={{ minWidth: 220 }}
            >
              <option value="all">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.shop_name || v.owner_name} ({v.email})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="admin-filters-right">
          <div className="admin-results-count">{products.length} of {total} products</div>
          {selectedProducts.length > 0 && (
            <div className="admin-bulk-actions">
              <span className="admin-selected-count">{selectedProducts.length} selected</span>
              <button className="btn btn-secondary" onClick={() => setShowBulkActions(!showBulkActions)}>
                <Settings size={16} />
                Bulk Actions
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toasts shown above the product table */}
      {!!toasts.length && (
        <div style={{ display: 'grid', gap: 8, margin: '12px 0' }}>
          {toasts.map(t => (
            <div key={t.id} style={{ padding: '10px 14px', borderRadius: 8, color: '#0b1f0b', background: t.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(220,38,38,0.15)', border: `1px solid ${t.type === 'success' ? '#10b981' : '#dc2626'}` }}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}

      {showBulkActions && selectedProducts.length > 0 && (
        <div className="admin-bulk-actions-panel">
          <div className="admin-bulk-actions-content">
            <h4>Bulk Actions ({selectedProducts.length} products selected)</h4>
            <div className="admin-bulk-controls">
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="admin-filter-select">
                <option value="">Select Action</option>
                <option value="active">Set Active</option>
                <option value="inactive">Set Inactive</option>
                <option value="discontinued">Set Discontinued</option>
                <option value="delete">Delete</option>
              </select>
              <button className="btn btn-primary" onClick={runBulk} disabled={!bulkAction}>Apply</button>
              <button className="btn btn-secondary" onClick={clearSel}>Clear</button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-vendors-table-container">
        <table className="admin-vendors-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Vendor ID</th>
              <th>Vendor</th>
              <th>Category</th>
              <th>Cost Price</th>
              <th>Selling Price</th>
              <th>MRP</th>
              <th>Discount</th>
              <th>On Hand</th>
              <th>Reserved</th>
              <th>Available</th>
              <th>Images</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && products.map(p => (
              <React.Fragment key={p.id}>
                {editingProduct && editingProduct.id === p.id && (
                  <tr className="inline-edit-form-row">
                    <td colSpan={15} style={{ padding: 0, backgroundColor: '#f9fafb' }}>
                      <div style={{ padding: '20px', border: '2px solid #0066ff', borderRadius: '8px', margin: '8px', backgroundColor: '#fff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h3 style={{ margin: 0, color: '#0066ff' }}>Edit Product: {p.name}</h3>
                          <button 
                            className="btn btn-secondary" 
                            onClick={cancelEdit}
                            style={{ padding: '6px 12px', fontSize: '14px' }}
                          >
                            <X size={16} style={{ marginRight: '4px' }} />
                            Cancel
                          </button>
                        </div>
                        <form onSubmit={saveProduct} className="product-form" style={{ maxWidth: '100%' }}>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Product Name *</label>
                              <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                            </div>
                            <div className="form-group">
                              <label>SKU *</label>
                              <input type="text" name="sku" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} required />
                              <small>
                                SKU is automatically generated when you change the category. Current format: SONI-{'{VendorID}'}-{'{CategoryCode}'}-{'{0001}'}. You can edit manually if needed.
                              </small>
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Vendor *</label>
                              <select name="vendor_id" value={formData.vendor_id} onChange={handleVendorChange} required>
                                <option value="">Select Vendor</option>
                                {vendors.map(v => (
                                  <option key={v.id} value={v.id}>{v.shop_name || v.owner_name} ({v.email})</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Category *</label>
                              <select name="category" value={formData.category} onChange={handleCategoryChange} required>
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                  <option key={getCategoryId(category)} value={getCategoryName(category)}>
                                    {getCategoryName(category)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Cost Price *</label>
                              <input type="number" step="0.01" name="cost_price" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} required />
                            </div>
                            <div className="form-group">
                              <label>Selling Price</label>
                              <input
                                type="number"
                                step="0.01"
                                name="price"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                              />
                              <small>
                                Set the platform selling price. Discount is applied automatically when MRP is higher.
                              </small>
                            </div>
                            <div className="form-group">
                              <label>MRP</label>
                              <input
                                type="number"
                                step="0.01"
                                name="mrp"
                                value={formData.mrp}
                                onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                              />
                              <small>
                                {discountPreview
                                  ? `Discount vs MRP: ${formatCurrency(discountPreview.amount)} (${discountPreview.percent}%)`
                                  : 'Enter an MRP above the selling price to show a discount.'}
                              </small>
                            </div>
                          </div>

                          {(formData.price || formData.mrp) && (
                            <div className="form-row" style={{ marginTop: -12 }}>
                              <div className="form-group" style={{ flex: 1 }}>
                                <label>Pricing Summary</label>
                                <div className="pricing-summary" style={{ padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span>Selling Price</span>
                                    <strong>{formatCurrency(formData.price)}</strong>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span>MRP</span>
                                    <strong>{formatCurrency(formData.mrp)}</strong>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Discount</span>
                                    <strong>{discountPreview ? `${formatCurrency(discountPreview.amount)} (${discountPreview.percent}%)` : '-'}</strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="form-row">
                            <div className="form-group">
                              <label>HSN Code</label>
                              <input 
                                type="text" 
                                name="hsn_code" 
                                value={formData.hsn_code} 
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  if (value.length <= 5) {
                                    setFormData({ ...formData, hsn_code: value });
                                  }
                                }}
                                maxLength="5"
                                placeholder="Enter 3-5 digit HSN code"
                              />
                              <small>
                                HSN code is automatically updated when you change the category. You can edit manually if needed.
                              </small>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Description</label>
                            <textarea name="description" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Unit</label>
                              <select name="unit" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
                                <option value="piece">Piece</option>
                                <option value="kg">Kilogram</option>
                                <option value="g">Gram</option>
                                <option value="l">Liter</option>
                                <option value="ml">Milliliter</option>
                                <option value="box">Box</option>
                                <option value="pack">Pack</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Weight (kg)</label>
                              <input type="number" step="0.01" name="weight" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Image URL</label>
                              <input type="url" name="image_url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Product Gallery</label>
                            <div className="product-images-manager">
                              {(formData.product_images || []).length > 0 ? (
                                <div className="product-images-grid">
                                  {formData.product_images.map((img, idx) => (
                                    <div key={`${img}-${idx}`} className="product-image-thumb">
                                      <img src={img} alt={`Product ${idx + 1}`} />
                                      <button type="button" className="product-image-remove" onClick={() => removeImage(idx)}>
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="product-image-empty">
                                  <span>No images uploaded yet</span>
                                </div>
                              )}
                              <label className="upload-btn">
                                <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                                {uploadingImages ? 'Uploading...' : 'Upload images'}
                              </label>
                              <small>The first image is used as the primary image. You can also paste an image URL above.</small>
                            </div>
                          </div>

                          <div className="inventory-section">
                            <h3>Inventory Settings</h3>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Stock On Hand</label>
                                <input type="number" name="stock_on_hand" value={formData.stock_on_hand} onChange={(e) => setFormData({ ...formData, stock_on_hand: e.target.value })} min="0" />
                              </div>
                              <div className="form-group">
                                <label>Min Stock Level</label>
                                <input type="number" name="min_stock_level" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })} min="0" />
                              </div>
                            </div>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Reorder Point</label>
                                <input type="number" name="reorder_point" value={formData.reorder_point} onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })} min="0" />
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Reorder Quantity</label>
                              <input type="number" name="reorder_quantity" value={formData.reorder_quantity} onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })} min="0" />
                            </div>
                          </div>

                          <div className="modal-actions" style={{ marginTop: '16px' }}>
                            <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
                            <button type="submit" className="btn btn-primary">Update Product</button>
                          </div>
                        </form>
                      </div>
                    </td>
                  </tr>
                )}
                <tr key={p.id}>
                  <td>
                    <div className="admin-vendor-info">
                      <div className="admin-vendor-avatar">{(p.name || 'P').charAt(0)}</div>
                      <div>
                        <div className="admin-vendor-name">{p.name}</div>
                        <div className="admin-vendor-location">SKU: {p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ color: '#065f46', fontWeight: 600 }}>
                      {vendorIdLookup[p.vendor_id] || p.vendor_code || p.vendor_id || 'N/A'}
                    </span>
                  </td>
                  <td>{p.vendor_name || p.owner_name || '-'}</td>
                  <td>{p.category}</td>
                  <td>{formatCurrency(p.cost_price)}</td>
                  <td>{formatCurrency(p.price)}</td>
                  <td>{formatCurrency(p.mrp)}</td>
                  <td>
                    {(() => {
                      const discount = getDiscountDetails(p.price, p.mrp);
                      if (!discount) return '-';
                      return `${formatCurrency(discount.amount)} (${discount.percent}%)`;
                    })()}
                  </td>
                  <td>{p.stock_on_hand ?? 0}</td>
                  <td>{p.stock_reserved ?? 0}</td>
                  <td>{p.stock_available ?? 0}</td>
                  <td>
                    <div className="product-images-display">
                      {(() => {
                        // Safely parse product_images if it's a string
                        let images = p.product_images;
                        if (typeof images === 'string' && images.trim()) {
                          try {
                            images = JSON.parse(images);
                          } catch (e) {
                            images = [];
                          }
                        }
                        // Fallback to image_url if no product_images
                        if ((!images || !Array.isArray(images) || images.length === 0) && p.image_url) {
                          images = [p.image_url];
                        }
                        return images && Array.isArray(images) && images.length > 0 ? (
                          <div className="images-container">
                            <img 
                              src={images[0]} 
                              alt={p.name}
                              className="product-image-thumbnail"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="no-image-fallback" 
                              style={{ display: 'none' }}
                            >
                              <span>No Image</span>
                            </div>
                            <div className="image-download-overlay">
                              <button 
                                className="download-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(images[0], p.name);
                                }}
                                title="Download Image"
                              >
                                <Download size={16} />
                              </button>
                            </div>
                            {images.length > 1 && (
                              <div className="additional-images-count">
                                +{images.length - 1}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="no-image-placeholder">
                            <span>No Image</span>
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-status-badge ${p.status === 'active' ? 'status-active' : p.status === 'inactive' ? 'status-suspended' : 'status-blocked'}`}>{p.status}</span>
                  </td>
                  <td>{p.created_at ? new Date(p.created_at).toLocaleString() : '-'}</td>
                  <td>
                    <div className="admin-vendor-actions">
                      <button className="admin-action-btn" title="Edit" onClick={() => openEdit(p)}><Edit2 size={16} /></button>
                      <button className="admin-action-btn" title={p.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatus(p)}>
                        {p.status === 'active' ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                      </button>
                      <button className="admin-action-btn block" title="Delete" onClick={() => deleteOne(p.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {loading && (
              <tr><td colSpan={16} style={{ padding: 24, textAlign: 'center' }}>Loading...</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr><td colSpan={16} style={{ padding: 24, textAlign: 'center' }}>No products found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>Rows:</span>
          <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="pagination">
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="page-info">Page {page} of {Math.max(1, Math.ceil(total / limit))}</span>
          <button className="btn btn-secondary" disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>

      {showModal && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            // Close modal when clicking on overlay (not on modal content)
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button className="close-btn" onClick={closeModal} aria-label="Close modal">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveProduct} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>SKU *</label>
                  <input type="text" name="sku" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} required />
                  <small>
                    {editingProduct 
                      ? 'SKU is automatically generated when you change the category. Current format: SONI-{VendorID}-{CategoryCode}-{0001}. You can edit manually if needed.'
                      : 'SKU is automatically generated in format: SONI-{VendorID}-{CategoryCode}-{0001}. Select vendor and category to generate.'
                    }
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vendor *</label>
                  <select name="vendor_id" value={formData.vendor_id} onChange={handleVendorChange} required>
                    <option value="">Select Vendor</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.shop_name || v.owner_name} ({v.email})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select name="category" value={formData.category} onChange={handleCategoryChange} required>
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={getCategoryId(category)} value={getCategoryName(category)}>
                        {getCategoryName(category)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cost Price *</label>
                  <input type="number" step="0.01" name="cost_price" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Selling Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                  <small>
                    Set the platform selling price. Discount is applied automatically when MRP is higher.
                  </small>
                </div>
                <div className="form-group">
                  <label>MRP</label>
                  <input
                    type="number"
                    step="0.01"
                    name="mrp"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                  />
                  <small>
                    {discountPreview
                      ? `Discount vs MRP: ${formatCurrency(discountPreview.amount)} (${discountPreview.percent}%)`
                      : 'Enter an MRP above the selling price to show a discount.'}
                  </small>
                </div>
              </div>

              {(formData.price || formData.mrp) && (
                <div className="form-row" style={{ marginTop: -12 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Pricing Summary</label>
                    <div className="pricing-summary" style={{ padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span>Selling Price</span>
                        <strong>{formatCurrency(formData.price)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span>MRP</span>
                        <strong>{formatCurrency(formData.mrp)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Discount</span>
                        <strong>{discountPreview ? `${formatCurrency(discountPreview.amount)} (${discountPreview.percent}%)` : '-'}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>HSN Code</label>
                  <input 
                    type="text" 
                    name="hsn_code" 
                    value={formData.hsn_code} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
                      if (value.length <= 5) { // Limit to 5 digits
                        setFormData({ ...formData, hsn_code: value });
                      }
                    }}
                    maxLength="5"
                    placeholder="Enter 3-5 digit HSN code"
                  />
                  <small>
                    {editingProduct 
                      ? 'HSN code is automatically updated when you change the category. You can edit manually if needed.'
                      : 'HSN code is automatically set based on selected category'
                    }
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unit</label>
                  <select name="unit" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
                    <option value="piece">Piece</option>
                    <option value="kg">Kilogram</option>
                    <option value="g">Gram</option>
                    <option value="l">Liter</option>
                    <option value="ml">Milliliter</option>
                    <option value="box">Box</option>
                    <option value="pack">Pack</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input type="number" step="0.01" name="weight" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image URL</label>
                  <input type="url" name="image_url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Product Gallery</label>
                <div className="product-images-manager">
                  {(formData.product_images || []).length > 0 ? (
                    <div className="product-images-grid">
                      {formData.product_images.map((img, idx) => (
                        <div key={`${img}-${idx}`} className="product-image-thumb">
                          <img src={img} alt={`Product ${idx + 1}`} />
                          <button type="button" className="product-image-remove" onClick={() => removeImage(idx)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="product-image-empty">
                      <span>No images uploaded yet</span>
                    </div>
                  )}
                  <label className="upload-btn">
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                    {uploadingImages ? 'Uploading...' : 'Upload images'}
                  </label>
                  <small>The first image is used as the primary image. You can also paste an image URL above.</small>
                </div>
              </div>

              <div className="inventory-section">
                <h3>Inventory Settings</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Stock On Hand</label>
                    <input type="number" name="stock_on_hand" value={formData.stock_on_hand} onChange={(e) => setFormData({ ...formData, stock_on_hand: e.target.value })} min="0" />
                  </div>
                  <div className="form-group">
                    <label>Min Stock Level</label>
                    <input type="number" name="min_stock_level" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Reorder Point</label>
                    <input type="number" name="reorder_point" value={formData.reorder_point} onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })} min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Reorder Quantity</label>
                  <input type="number" name="reorder_quantity" value={formData.reorder_quantity} onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })} min="0" />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingProduct ? 'Update Product' : 'Add Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;


